import React from "react";
import { AlertTriangle } from "lucide-react";

type State = { error: Error | null; info: React.ErrorInfo | null };

export default class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Dashboard error", error, info);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    if (this.state.error) {
      const err = this.state.error;
      // Surface the error name + message + first ~25 stack frames + the
      // React component stack. Without this, mobile users hit a generic
      // "出錯了" toast and the only forensics we have is the minified
      // expression name (which is per-build random). Showing the full
      // stack means a screenshot is enough to pin the culprit even on
      // a phone with no devtools.
      const stack = (err.stack ?? "").split("\n").slice(0, 25).join("\n");
      const componentStack = this.state.info?.componentStack ?? "";
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg-base p-6">
          <div className="max-w-2xl w-full rounded-lg border border-danger/40 bg-danger-subtle p-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-danger" />
              <h2 className="text-lg font-semibold text-text-primary">出錯了</h2>
            </div>
            <p className="text-sm text-text-primary font-semibold mb-2 break-words">
              {err.name || "Error"}: {err.message || "未預期的錯誤"}
            </p>
            <details open className="mt-3">
              <summary className="text-xs text-text-secondary cursor-pointer mb-1">JavaScript stack（請完整截圖回報）</summary>
              <pre className="text-2xs text-text-muted whitespace-pre-wrap break-all max-h-48 overflow-auto bg-bg-base p-2 rounded border border-border">
                {stack || "(no stack)"}
              </pre>
            </details>
            {componentStack && (
              <details className="mt-2">
                <summary className="text-xs text-text-secondary cursor-pointer mb-1">React 組件樹</summary>
                <pre className="text-2xs text-text-muted whitespace-pre-wrap break-all max-h-32 overflow-auto bg-bg-base p-2 rounded border border-border">
                  {componentStack}
                </pre>
              </details>
            )}
            <div className="mt-4 flex gap-2">
              <button onClick={this.reset} className="btn-secondary text-sm">
                重試
              </button>
              <button
                onClick={() => { window.location.href = "/dashboard"; }}
                className="btn text-sm"
              >
                回首頁
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
